// Services > Channels > Routes — the tenant's surface for inbound endpoints.
// It lives HERE, in the control-plane Application, not on the operator Desk: the
// Desk is the operator surface, administration is the Application's. The layers
// below are the owner's LAYERED model, so each is controllable on its own and
// never smeared together:
//   1. Providers  — a read-only status strip (Vonage / ACS / web): configured?,
//      last seen working and adaptor capabilities. No credential management here.
//   2. Adaptors   — the connector capabilities, shown as chips inside each
//      provider card (available once the provider is configured).
//   3. Numbers    — the provider's owned inventory (phone numbers via the
//      account API, WhatsApp senders, widget keys), joined against routes so
//      each reads as assigned (→ its route) or free (→ create a route from it).
//   4. Routes     — the channel_endpoint CRUD (identifier → entry flow, health,
//      appearance + snippet). A number is NOT a route.
// The API is the real boundary — /api/services/channels reads for any member,
// writes only for a tenant admin (requireTenantAdmin + audit) — so this page's
// `admin` flag only decides which controls render, never what is allowed.
import { useContext, useEffect, useMemo, useState } from 'react'
import { isTenantAdmin, AuthContext } from '@retink/web-auth'
// The bare barrel re-exports the server-only session codec (node:crypto) — browser code must
// import the ./appearance subpath.
import { mergeAppearance, type WebchatAppearance } from '@retink/connector-webchat/appearance'
import { Button, ConfirmDestructive, Field, Input, Switch, PageHeader, EmptyState, Badge, useResource, toast } from '@retink/web-ui'
import {
  Plus, MessageSquare, Smartphone, Globe, Copy, Check, Phone, KeyRound,
  CheckCircle2, AlertCircle, Clock3, ArrowUpRight, Webhook,
} from 'lucide-react'
import {
  channels, type ChannelEndpoint, type PublishedFlow, type ProviderStatus,
  type NumberInventory,
} from '../lib/channels-api'
import { WidgetPreview } from '../components/WidgetPreview'

// The widget is a MACHINE artifact: embed snippets must point customers at the
// machine host (channels.*), not the control-plane host this page runs on.
// Provider callback URLs use hooks.* because they live in third-party systems.
// Same-suffix mapping from app.* covers dev and prod without config.
const WEBCHAT_ORIGIN =
  import.meta.env['VITE_WEBCHAT_URL'] ?? window.location.origin.replace('//app.', '//channels.')
const HOOKS_ORIGIN =
  import.meta.env['VITE_HOOKS_URL'] ?? window.location.origin.replace('//app.', '//hooks.')

// One place turns a failed mutation into an operator-visible message.
function reportError(e: unknown) {
  toast.error((e as Error)?.message || 'Something went wrong')
}
const selectClass =
  'h-9 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'

// The channel types this surface offers, each a (provider, medium,
// config.medium) triple. WhatsApp rides the record channel 'chat'; its Vonage
// connector medium is 'whatsapp'. ACS SMS is single-medium with resource-level
// auth (no per-route secrets), so it has no connectorMedium config discriminator.
type ChannelType = 'webchat' | 'vonage_sms' | 'vonage_whatsapp' | 'acs_sms' | 'acs_voice' | 'http_api'
const CHANNEL_TYPES: Record<ChannelType, { label: string; provider: string; medium: string; connectorMedium?: string; icon: typeof Globe }> = {
  webchat: { label: 'Web chat', provider: 'web', medium: 'chat', icon: Globe },
  vonage_sms: { label: 'Vonage SMS', provider: 'vonage', medium: 'sms', connectorMedium: 'sms', icon: Smartphone },
  vonage_whatsapp: { label: 'Vonage WhatsApp', provider: 'vonage', medium: 'chat', connectorMedium: 'whatsapp', icon: MessageSquare },
  acs_sms: { label: 'ACS SMS', provider: 'acs', medium: 'sms', icon: Smartphone },
  acs_voice: { label: 'ACS Voice', provider: 'acs', medium: 'voice', icon: Phone },
  // Display-only: webhook routes are created by publishing a flow with an HTTP
  // trigger, never from this surface (excluded from the create dropdown).
  http_api: { label: 'API webhook', provider: 'http', medium: 'api', icon: Webhook },
}
const CREATABLE_CHANNEL_TYPES = Object.entries(CHANNEL_TYPES).filter(([k]) => k !== 'http_api')

function channelTypeOf(ep: ChannelEndpoint): ChannelType {
  if (ep.provider === 'acs') return ep.medium === 'voice' ? 'acs_voice' : 'acs_sms'
  if (ep.provider === 'vonage') return (ep.config['medium'] === 'whatsapp' || ep.medium === 'chat') && ep.config['medium'] !== 'sms' ? 'vonage_whatsapp' : 'vonage_sms'
  if (ep.provider === 'http') return 'http_api'
  return 'webchat'
}

function badge(ct: ChannelType): string {
  return CHANNEL_TYPES[ct].label
}

function ago(ts: string | null): string | null {
  if (!ts) return null
  const mins = Math.round((Date.now() - Date.parse(ts)) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`
  return `${Math.round(mins / 1440)}d ago`
}

function health(lastInboundAt: string | null): { label: string; tone: string } {
  if (!lastInboundAt) return { label: 'no events yet', tone: 'text-muted-foreground' }
  const age = Date.now() - Date.parse(lastInboundAt)
  return { label: `last event ${ago(lastInboundAt)}`, tone: age < 86_400_000 ? 'text-accent-text' : 'text-muted-foreground' }
}

type Prefill = { channelType: ChannelType; identifier: string }

export default function ChannelRoutes() {
  const { user } = useContext(AuthContext)
  // Reads are open to every member (the server says so); only the mutating
  // controls are an admin's. A member sees the surface instead of a redirect.
  const admin = isTenantAdmin(user)

  const endpointsRes = useResource(() => channels.listEndpoints(), [])
  const flowsRes = useResource(() => channels.listFlows(), [])
  const providersRes = useResource(() => channels.listProviders(), [])
  const numbersRes = useResource(() => channels.listNumbers('vonage'), [])
  const acsNumbersRes = useResource(() => channels.listNumbers('acs'), [])

  const [tab, setTab] = useState<'routes' | 'numbers'>('routes')
  const [draft, setDraft] = useState<ChannelEndpoint | 'new' | null>(null)
  const [prefill, setPrefill] = useState<Prefill | null>(null)

  const reloadAll = () => { endpointsRes.reload(); providersRes.reload(); numbersRes.reload(); acsNumbersRes.reload() }

  // Layer-crossing actions: open the route a number/identifier is bound to, or
  // start a new route prefilled from a free number.
  const openRoute = (endpointId: string) => {
    const ep = endpointsRes.data?.find((e) => e.id === endpointId)
    if (!ep) return
    setPrefill(null); setDraft(ep); setTab('routes')
  }
  const createRouteFrom = (p: Prefill) => { setPrefill(p); setDraft('new'); setTab('routes') }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Navigation comes from the shell's left rail (Services > Channels). */}
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
      <PageHeader
        title="Routes"
        description="Providers, numbers and routes as separate layers — connect a provider, see the numbers it owns, then route each to a flow."
        action={
          admin && tab === 'routes' ? (
            <Button onClick={() => { setPrefill(null); setDraft('new') }}>
              <Plus /> New route
            </Button>
          ) : undefined
        }
      />

      <ProviderStrip providers={providersRes.data ?? []} loading={providersRes.loading} />

      {/* Layer tabs — Numbers and Routes stay visually + data-model distinct. */}
      <div className="mb-4 flex gap-1 border-b border-border">
        <TabButton active={tab === 'routes'} onClick={() => setTab('routes')} label={`Routes${endpointsRes.data ? ` (${endpointsRes.data.length})` : ''}`} />
        <TabButton active={tab === 'numbers'} onClick={() => setTab('numbers')} label="Numbers & identifiers" />
      </div>

      {tab === 'routes' ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="flex flex-col gap-2">
            {endpointsRes.loading && <div className="text-sm text-muted-foreground">Loading routes…</div>}
            {endpointsRes.data && endpointsRes.data.length === 0 && (
              <EmptyState icon={MessageSquare} title="No routes yet" description="Add a web chat widget or route a Vonage number to a flow." />
            )}
            {endpointsRes.data?.map((ep) => {
              const ct = channelTypeOf(ep)
              const Icon = CHANNEL_TYPES[ct].icon
              const h = health(ep.lastInboundAt)
              return (
                <button
                  key={ep.id}
                  onClick={() => { setPrefill(null); setDraft(ep) }}
                  className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                    draft !== 'new' && draft?.id === ep.id ? 'border-accent-border bg-muted' : 'border-border bg-card hover:bg-muted'
                  }`}
                >
                  <Icon className="size-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground">{ep.identifier}</span>
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{badge(ct)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="truncate text-muted-foreground">
                        {ep.entryFlowId ? flowsRes.data?.find((f) => f.id === ep.entryFlowId)?.name ?? 'flow bound' : 'no flow bound'}
                      </span>
                      <span className={`shrink-0 ${h.tone}`}>· {h.label}</span>
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${ep.isActive ? 'bg-accent-fill text-accent-text' : 'bg-muted text-muted-foreground'}`}>
                    {ep.isActive ? 'live' : 'paused'}
                  </span>
                </button>
              )
            })}
          </div>

          <div>
            {draft ? (
              <EndpointEditor
                key={draft === 'new' ? `new-${prefill?.identifier ?? ''}` : draft.id}
                endpoint={draft === 'new' ? null : draft}
                prefill={draft === 'new' ? prefill : null}
                flows={flowsRes.data ?? []}
                admin={admin}
                onDone={() => { setDraft(null); setPrefill(null); reloadAll() }}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                {admin ? 'Select a route to edit, or add a new one.' : 'Select a route to see how it is configured.'}
              </div>
            )}
          </div>
        </div>
      ) : (
        <NumbersInventory
          numbers={numbersRes.data ?? null}
          loading={numbersRes.loading}
          acsNumbers={acsNumbersRes.data ?? null}
          acsLoading={acsNumbersRes.loading}
          endpoints={endpointsRes.data ?? []}
          admin={admin}
          onOpenRoute={openRoute}
          onCreateRoute={createRouteFrom}
        />
      )}
      </div>
    </div>
  )
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active ? 'border-accent-border text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}

// ─── Layer 1: Providers (read-only status strip) ─────────────────────────────
const PROVIDER_ICON: Record<string, typeof Globe> = { web: Globe, vonage: Smartphone, acs: Phone, http: Webhook }

function ProviderStrip({ providers, loading }: { providers: ProviderStatus[]; loading: boolean }) {
  return (
    <div className="mb-6">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Providers</div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading && providers.length === 0 && <div className="text-sm text-muted-foreground">Loading providers…</div>}
        {providers.map((p) => <ProviderCard key={p.provider} p={p} />)}
      </div>
    </div>
  )
}

function ProviderCard({ p }: { p: ProviderStatus }) {
  const Icon = PROVIDER_ICON[p.provider] ?? Globe
  const state =
    p.state === 'ready'
      ? { badge: 'success' as const, label: 'Configured', Icon: CheckCircle2, tone: 'text-accent-text' }
      : p.state === 'coming-soon'
        ? { badge: 'neutral' as const, label: 'Coming next', Icon: Clock3, tone: 'text-muted-foreground' }
        : { badge: 'warning' as const, label: 'Not configured', Icon: AlertCircle, tone: 'text-warning' }
  const last = ago(p.lastVerifiedAt)
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Icon className="size-5 shrink-0 text-muted-foreground" />
        <span className="font-medium text-foreground">{p.label}</span>
        <Badge tone={state.badge} className="ml-auto gap-1">
          <state.Icon className="size-3" /> {state.label}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-1">
        {p.adaptors.map((a) => (
          <span key={a.key} className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{a.label}</span>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{p.endpointCount} {p.endpointCount === 1 ? 'route' : 'routes'}</span>
        {last && <span>· last event {last}</span>}
      </div>
      {p.state === 'unconfigured' && p.missingSecretRefs.length > 0 && (
        <div className="text-xs text-warning">
          Missing secret{p.missingSecretRefs.length > 1 ? 's' : ''}: {p.missingSecretRefs.join(', ')}
        </div>
      )}
      {/* Credentials are managed on this surface — Vonage secret references in the
          route editor, ACS at the resource. The platform adapter store carries no
          channel rows; it lists other adapter types. */}
      {p.provider === 'vonage' && p.state !== 'ready' && (
        <div className="mt-auto text-xs text-muted-foreground">Add a route below to enter this provider's credentials.</div>
      )}
      {p.provider === 'acs' && p.state !== 'ready' && (
        <div className="mt-auto text-xs text-muted-foreground">Resource-level auth — set the ACS resource endpoint.</div>
      )}
    </div>
  )
}

// ─── Layer 3: Numbers & identifiers inventory ────────────────────────────────
function NumbersInventory({
  numbers, loading, acsNumbers, acsLoading, endpoints, admin, onOpenRoute, onCreateRoute,
}: {
  numbers: NumberInventory | null
  loading: boolean
  acsNumbers: NumberInventory | null
  acsLoading: boolean
  endpoints: ChannelEndpoint[]
  admin: boolean
  onOpenRoute: (endpointId: string) => void
  onCreateRoute: (p: Prefill) => void
}) {
  // WA senders + widget keys are themselves endpoint identifiers — derived from
  // the routes already loaded (a WA sender / widget key IS its route).
  const waSenders = endpoints.filter((e) => e.provider === 'vonage' && channelTypeOf(e) === 'vonage_whatsapp')
  const widgetKeys = endpoints.filter((e) => e.provider === 'web')

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PhoneNumbersSection
        providerLabel="Vonage" numbers={numbers} loading={loading} createChannelType="vonage_sms" admin={admin}
        emptyHint="No Vonage credentials yet. Add a Vonage route below and enter its API credentials, then the account's owned numbers appear here."
        errorHint="Couldn't reach Vonage to list numbers — check the credentials on the Vonage route."
        onOpenRoute={onOpenRoute} onCreateRoute={onCreateRoute}
      />
      <PhoneNumbersSection
        providerLabel="ACS" numbers={acsNumbers} loading={acsLoading} createChannelType="acs_sms" admin={admin}
        emptyHint="ACS isn't configured. Set the Communication Services resource endpoint (ACS_ENDPOINT), then its purchased numbers appear here."
        errorHint="Couldn't reach ACS to list numbers — check the resource configuration."
        onOpenRoute={onOpenRoute} onCreateRoute={onCreateRoute}
      />

      <IdentifierSection
        title="WhatsApp senders" icon={MessageSquare} kindLabel="WhatsApp"
        items={waSenders} onOpenRoute={onOpenRoute}
        empty="No WhatsApp senders — add a Vonage WhatsApp route."
      />
      <IdentifierSection
        title="Widget keys" icon={KeyRound} kindLabel="Web chat"
        items={widgetKeys} onOpenRoute={onOpenRoute}
        empty="No widget keys — add a web chat route."
      />
    </div>
  )
}

// One provider's owned phone numbers (assigned → its route, or free → create
// one). Same markup for Vonage and ACS — the inventory shape is identical; only
// the labels + the channel type a free number seeds differ.
//
// A number only offers "Create route" for a medium it was SOLD with (numbers
// are single-capability more often than not — an SMS-only mobile, a voice-only
// geographic line). A free number that can't carry the section's medium says so
// instead of offering a route that would never deliver; the server enforces the
// same rule (422) for API callers.
const CAPABILITY_FOR_MEDIUM: Record<string, string> = { sms: 'SMS', voice: 'VOICE' }

function PhoneNumbersSection({
  providerLabel, numbers, loading, createChannelType, admin, emptyHint, errorHint, onOpenRoute, onCreateRoute,
}: {
  providerLabel: string
  numbers: NumberInventory | null
  loading: boolean
  createChannelType: ChannelType
  admin: boolean
  emptyHint: string
  errorHint: string
  onOpenRoute: (endpointId: string) => void
  onCreateRoute: (p: Prefill) => void
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
        <Phone className="size-4 text-muted-foreground" /> Phone numbers
        <span className="text-xs font-normal text-muted-foreground">{providerLabel}</span>
      </div>
      {loading && <div className="text-sm text-muted-foreground">Loading numbers…</div>}
      {/* provider_error (the read THREW — bad creds / unreachable) is a fault and
          reads as a warning, visually distinct from not_configured (a normal
          "not set up yet" state) AND from a genuinely-empty available account
          (the EmptyState below). */}
      {!loading && numbers && !numbers.available && (
        numbers.reason === 'provider_error' ? (
          <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/5 p-6 text-sm text-warning">
            <AlertCircle className="mt-0.5 size-4 shrink-0" /> <span>{errorHint}</span>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">{emptyHint}</div>
        )
      )}
      {!loading && numbers?.available && numbers.numbers.length === 0 && (
        <EmptyState icon={Phone} title="No numbers on the account" description={`Buy a number in ${providerLabel}; it shows here once owned.`} />
      )}
      <div className="flex flex-col gap-2">
        {numbers?.available && numbers.numbers.map((n) => (
          <div key={n.msisdn} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
            <Phone className="size-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{n.msisdn}</span>
                {n.country && <span className="shrink-0 text-xs text-muted-foreground">{n.country}</span>}
              </div>
              <div className="flex flex-wrap gap-1">
                {n.capabilities.map((cap) => (
                  <span key={cap} className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{cap}</span>
                ))}
              </div>
            </div>
            {n.assignedEndpointId ? (
              <button onClick={() => onOpenRoute(n.assignedEndpointId!)} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent-text hover:underline">
                Assigned <ArrowUpRight className="size-3" />
              </button>
            ) : n.capabilities.includes(CAPABILITY_FOR_MEDIUM[CHANNEL_TYPES[createChannelType].medium] ?? '') ? (
              <>
                <Badge tone="outline" className="shrink-0">Free</Badge>
                {admin && (
                  <Button variant="outline" onClick={() => onCreateRoute({ channelType: createChannelType, identifier: n.msisdn })}>Create route</Button>
                )}
              </>
            ) : (
              <span className="shrink-0 text-xs text-muted-foreground">
                {n.capabilities.length ? `${n.capabilities.join(' + ')} only — no ${CHANNEL_TYPES[createChannelType].medium.toUpperCase()} route` : 'no routable capability'}
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

// WA senders + widget keys are identifiers that ARE their route — one section
// each, every row links straight to its route.
function IdentifierSection({
  title, icon: Icon, kindLabel, items, onOpenRoute, empty,
}: {
  title: string; icon: typeof Globe; kindLabel: string
  items: ChannelEndpoint[]; onOpenRoute: (id: string) => void; empty: string
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="size-4 text-muted-foreground" /> {title}
        <span className="text-xs font-normal text-muted-foreground">{kindLabel}</span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">{empty}</div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((e) => (
            <button key={e.id} onClick={() => onOpenRoute(e.id)} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-muted">
              <Icon className="size-5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">{e.identifier}</span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent-text">Open route <ArrowUpRight className="size-3" /></span>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

// ─── Layer 4: Route editor (unchanged behaviour; accepts a prefill for new) ───
function EndpointEditor({
  endpoint, prefill, flows, admin, onDone,
}: {
  endpoint: ChannelEndpoint | null
  prefill: Prefill | null
  flows: PublishedFlow[]
  admin: boolean
  onDone: () => void
}) {
  const [channelType, setChannelType] = useState<ChannelType>(endpoint ? channelTypeOf(endpoint) : prefill?.channelType ?? 'webchat')
  const [identifier, setIdentifier] = useState(endpoint?.identifier ?? prefill?.identifier ?? '')
  const [isActive, setIsActive] = useState(endpoint?.isActive ?? true)
  const [appearance, setAppearance] = useState<WebchatAppearance>(mergeAppearance(endpoint?.appearance))
  const [config, setConfig] = useState<Record<string, string>>(() => coerceConfig(endpoint?.config))
  const [saving, setSaving] = useState(false)
  const [liveCount, setLiveCount] = useState<number | null>(null)

  // The entry flow is no longer set here (voice-process-review #4): publishing a
  // flow with this number in its Voice Trigger DOES the bind. This surface shows
  // the assigned flow read-only + manages inventory (create/delete).
  const assignedFlow = endpoint?.entryFlowId ? flows.find((f) => f.id === endpoint.entryFlowId) : undefined
  const assignedFlowLabel = endpoint?.entryFlowId
    ? (assignedFlow ? `${assignedFlow.name} (v${assignedFlow.version})` : endpoint.entryFlowId)
    : 'None'

  const isWebchat = channelType === 'webchat'
  const isAcs = CHANNEL_TYPES[channelType].provider === 'acs'
  const isHttp = channelType === 'http_api'
  const setAppear = <K extends keyof WebchatAppearance>(k: K, v: WebchatAppearance[K]) => setAppearance((a) => ({ ...a, [k]: v }))
  const setCfg = (k: string, v: string) => setConfig((c) => ({ ...c, [k]: v }))

  useEffect(() => {
    if (!endpoint) return
    channels.impact(endpoint.id).then((r) => setLiveCount(r.liveConversations)).catch(() => setLiveCount(null))
  }, [endpoint])

  const snippet = useMemo(
    () =>
      `<script src="${WEBCHAT_ORIGIN}/webchat.js"></script>\n` +
      `<script>RetinkWebchat.mount({ endpointKey: ${JSON.stringify(identifier || 'your-key')}, apiBase: ${JSON.stringify(WEBCHAT_ORIGIN)} })</script>`,
    [identifier],
  )

  function buildConfig(): Record<string, unknown> {
    const t = CHANNEL_TYPES[channelType]
    // ACS auth is resource-level → no per-route secrets; just the medium.
    if (t.provider === 'acs') return { medium: 'sms' }
    if (t.provider !== 'vonage') return {}
    const out: Record<string, unknown> = { medium: t.connectorMedium }
    for (const [k, v] of Object.entries(config)) if (v.trim()) out[k] = k === 'idleCloseHours' ? Number(v) : v.trim()
    return out
  }

  async function save() {
    setSaving(true)
    try {
      const t = CHANNEL_TYPES[channelType]
      // entry_flow_id is bound by flow publish, never here — inventory only.
      if (endpoint) {
        // http routes: send only isActive — identifier is publish-composed
        // ({tenant}/{flow}, rejected by the slug validator), and config carries
        // the webhook secretHash the flow publish minted.
        await channels.update(endpoint.id, {
          ...(isHttp ? {} : { identifier, ...(isWebchat ? { appearance } : { config: buildConfig() }) }),
          isActive,
        })
      } else {
        await channels.create({
          identifier, provider: t.provider, medium: t.medium,
          ...(isWebchat ? { appearance } : { config: buildConfig() }),
        })
      }
      onDone()
    } catch (e) {
      reportError(e)
    } finally {
      setSaving(false)
    }
  }

  // Destructive delete is type-to-confirm (house rule) — armed with the live
  // impact the caller already fetched.
  const [confirmDelete, setConfirmDelete] = useState(false)

  async function remove() {
    if (!endpoint) return
    try {
      await channels.remove(endpoint.id)
      onDone()
    } catch (e) {
      reportError(e)
    }
  }

  const idPlaceholder = channelType === 'webchat' ? 'support-web' : channelType === 'acs_sms' ? '+18335551234' : '447700900123'
  const idLabel =
    channelType === 'webchat' ? 'Widget key'
      : channelType === 'http_api' ? 'Webhook path (set by flow publish)'
        : channelType === 'vonage_sms' ? 'Phone number (MSISDN, no +)'
          : channelType === 'acs_sms' ? 'ACS phone number (E.164, with +)'
            : 'WhatsApp sender number'

  return (
    <div className="grid gap-6 rounded-lg border border-border bg-card p-5 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex flex-col gap-4">
        {!endpoint && (
          <Field label="Channel type">
            <select className={selectClass} value={channelType} onChange={(e) => setChannelType(e.target.value as ChannelType)} disabled={saving || !admin}>
              {CREATABLE_CHANNEL_TYPES.map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </Field>
        )}

        <Field label={idLabel}>
          <Input value={identifier} onChange={(e) => setIdentifier(e.target.value.toLowerCase())} placeholder={idPlaceholder} disabled={saving || isHttp || !admin} />
        </Field>

        <Field label="Assigned flow">
          <div className="flex h-9 items-center rounded-md border border-input bg-muted px-2 text-sm text-muted-foreground">
            {assignedFlowLabel}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Assigned by flow publish — publish a flow with this number in its trigger to bind it.
          </p>
        </Field>

        {isWebchat ? (
          <>
            <div className="border-t border-border pt-3">
              <div className="mb-2 text-sm font-medium text-foreground">Appearance</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Primary colour">
                  <input type="color" value={appearance.primaryColor} onChange={(e) => setAppear('primaryColor', e.target.value)} className="h-9 w-full cursor-pointer rounded-md border border-border bg-card" />
                </Field>
                <Field label="Theme">
                  <select className={selectClass} value={appearance.theme} onChange={(e) => setAppear('theme', e.target.value as WebchatAppearance['theme'])}>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto (visitor's OS)</option>
                  </select>
                </Field>
                <Field label="Launcher position">
                  <select className={selectClass} value={appearance.position} onChange={(e) => setAppear('position', e.target.value as WebchatAppearance['position'])}>
                    <option value="bottom-right">Bottom right</option>
                    <option value="bottom-left">Bottom left</option>
                  </select>
                </Field>
                <Field label="Header title">
                  <Input value={appearance.headerTitle} onChange={(e) => setAppear('headerTitle', e.target.value)} placeholder="Chat" />
                </Field>
              </div>
              <Field label="Greeting" className="mt-3">
                <Input value={appearance.greeting} onChange={(e) => setAppear('greeting', e.target.value)} placeholder="Hi! How can we help?" />
              </Field>
            </div>
            <div className="border-t border-border pt-3">
              <div className="mb-2 text-sm font-medium text-foreground">Install snippet</div>
              <CopyField text={snippet} />
            </div>
          </>
        ) : isHttp ? (
          <div className="border-t border-border pt-3">
            <div className="mb-1 text-sm font-medium text-foreground">Webhook</div>
            <p className="mb-2 text-xs text-muted-foreground">
              Created and bound by publishing a flow with an HTTP trigger. Authentication is selected
              on that trigger; reusable credentials live in Creator under Flow Auths.
            </p>
            {endpoint?.webhookUrl && (
              <p className="mt-1 text-xs text-muted-foreground">
                Webhook URL: <code className="rounded bg-muted px-1 py-0.5">{endpoint.webhookUrl}</code>
              </p>
            )}
          </div>
        ) : isAcs ? (
          <div className="border-t border-border pt-3">
            <div className="mb-1 text-sm font-medium text-foreground">Provider configuration</div>
            <p className="mb-2 text-xs text-muted-foreground">
              ACS uses <em>resource-level</em> authentication (Managed Identity / Azure credential) — there
              are no per-route secrets to enter here. Configure the Communication Services resource in the Azure portal.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Event Grid webhook:{' '}
              <code className="rounded bg-muted px-1 py-0.5">{HOOKS_ORIGIN}/webhooks/acs/events/&lt;secret&gt;</code>{' '}
              — subscribe the resource's SMSReceived + SMSDeliveryReportReceived events to it.
            </p>
          </div>
        ) : (
          <div className="border-t border-border pt-3">
            <div className="mb-1 text-sm font-medium text-foreground">Provider configuration</div>
            <p className="mb-2 text-xs text-muted-foreground">
              Secret <em>reference names</em> only — the values are provisioned in your secret store / env, never stored here.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Application ID"><Input value={config['applicationId'] ?? ''} onChange={(e) => setCfg('applicationId', e.target.value)} placeholder="uuid" /></Field>
              <Field label="API host (optional)"><Input value={config['apiHost'] ?? ''} onChange={(e) => setCfg('apiHost', e.target.value)} placeholder="api.nexmo.com" /></Field>
              <Field label="Signature secret ref"><Input value={config['signatureSecretRef'] ?? ''} onChange={(e) => setCfg('signatureSecretRef', e.target.value)} placeholder="VONAGE_SIGNATURE_SECRET" /></Field>
              <Field label="Private key ref (prod)"><Input value={config['privateKeySecretRef'] ?? ''} onChange={(e) => setCfg('privateKeySecretRef', e.target.value)} placeholder="VONAGE_PRIVATE_KEY" /></Field>
              <Field label="API key ref (dev)"><Input value={config['apiKeySecretRef'] ?? ''} onChange={(e) => setCfg('apiKeySecretRef', e.target.value)} placeholder="VONAGE_API_KEY" /></Field>
              <Field label="API secret ref (dev)"><Input value={config['apiSecretSecretRef'] ?? ''} onChange={(e) => setCfg('apiSecretSecretRef', e.target.value)} placeholder="VONAGE_API_SECRET" /></Field>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Webhook URL: <code className="rounded bg-muted px-1 py-0.5">{HOOKS_ORIGIN}/webhooks/vonage/messages</code> (inbound + delivery status).
            </p>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-4">
          <div className="flex items-center gap-2">
            {endpoint && (
              <>
                <Switch checked={isActive} onCheckedChange={setIsActive} label="Route live" disabled={!admin} />
                <span className="text-sm text-muted-foreground">{isActive ? 'Live' : 'Paused'}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {endpoint && admin && <Button variant="destructive" onClick={() => setConfirmDelete(true)}>Delete</Button>}
            {endpoint && admin && (
              <ConfirmDestructive
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Delete route"
                description={
                  liveCount && liveCount > 0
                    ? `${liveCount} live conversation${liveCount === 1 ? '' : 's'} will be disrupted. Inbound messages to this identifier will no longer reach a flow.`
                    : 'Inbound messages to this identifier will no longer reach a flow.'
                }
                confirmPhrase={endpoint.identifier}
                confirmLabel="Delete route"
                onConfirm={remove}
              />
            )}
            <Button variant="outline" onClick={onDone}>{admin ? 'Cancel' : 'Close'}</Button>
            {admin && (
              <Button onClick={save} disabled={saving || !identifier}>{saving ? 'Saving…' : endpoint ? 'Save' : 'Create'}</Button>
            )}
          </div>
        </div>
      </div>

      {isWebchat && (
        <div className="flex justify-center md:pl-2">
          <WidgetPreview appearance={appearance} />
        </div>
      )}
    </div>
  )
}

function coerceConfig(config: Record<string, unknown> | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(config ?? {})) if (k !== 'medium' && v != null) out[k] = String(v)
  return out
}

function CopyField({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="relative">
      <pre className="max-h-32 overflow-auto rounded-md border border-border bg-muted p-3 pr-11 text-xs text-foreground">{text}</pre>
      <button
        type="button"
        aria-label="Copy install snippet"
        onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
        className="absolute right-2 top-2 rounded-md border border-border bg-card p-1.5 text-muted-foreground hover:text-foreground"
      >
        {copied ? <Check className="size-4 text-accent-text" /> : <Copy className="size-4" />}
      </button>
    </div>
  )
}
